<template>
  <section id="information" class="info-container">
    <div v-if="loading" class="loading-state">Carregando informações...</div>

    <div 
      v-else
      v-for="card in infoCards" 
      :key="card.id" 
      class="info-card"
      :style="{ backgroundColor: card.bgColor, color: card.textColor }"
      :class="{ 'row-reverse': card.reverse }"
    >
      <div class="info-content">
        <h2 class="info-title" v-html="card.title"></h2>
        <div class="info-text ql-editor line-clamp" v-html="card.text"></div>
        
        <button @click="openModal(card)" class="read-more-btn text-cyan-400 hover:text-cyan-600">
          Continue lendo...
        </button>
      </div>
      
      <div class="info-image-wrapper">
        <img :src="card.imageUrl" :alt="card.title" class="info-img">
      </div>
    </div>

    <Transition name="fade">
      <div v-if="selectedCard" class="modal-overlay" @click.self="closeModal">
        <div class="modal-content" :style="{ backgroundColor: selectedCard.bgColor, color: selectedCard.textColor }">
          <button class="close-btn" @click="closeModal">✕</button>
          <img :src="selectedCard.imageUrl" class="modal-header-img" v-if="selectedCard.imageUrl">
          <div class="modal-body">
            <div class="modal-title" v-html="selectedCard.title"></div>
            <div class="ql-editor" v-html="selectedCard.text"></div>
          </div>
        </div>
      </div>
    </Transition>
  </section>
</template>

<script setup lang="ts">
  import { ref, onMounted } from 'vue';

  const HYGRAPH_ENDPOINT = process.env.VUE_APP_HYGRAPH_URL;

  interface InfoCard {
    id: string;
    title: string;
    text: string;
    bgColor: string;
    textColor: string;
    reverse: boolean;
    imageUrl: string;
  }

  const selectedCard = ref<InfoCard | null>(null);
  const infoCards = ref<InfoCard[]>([]);
  const loading = ref(true);

  const fetchInfoCards = async () => {
    const query = `
      query GetInfoCards {
        informations {
          id
          title
          text
          bgColor { hex }
          textColor { hex }
          reverse
          visible
          images { url }
        }
      }
    `;

    try {
      const response = await fetch(HYGRAPH_ENDPOINT!, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query })
      });
      
      const { data } = await response.json();

      

      if (data && data.informations) 
      {
        infoCards.value = data.informations.filter(item => item.visible).map((item: any) => ({
          id: item.id,
          title: item.title,
          text: item.text,
          reverse: item.reverse,
          bgColor: item.bgColor?.hex || '#F5F5F0', 
          textColor: item.textColor?.hex || '#333',
          imageUrl: item.images?.[0]?.url || item.images?.url || ''
        }));
      }
    } catch (error) {
      console.error("Erro ao carregar InfoCards:", error);
    } finally {
      loading.value = false;
    }
  };

  const openModal = (card: InfoCard) => {
    selectedCard.value = card;
    document.body.style.overflow = 'hidden'; // Trava o scroll do fundo
  };

  const closeModal = () => {
    selectedCard.value = null;
    document.body.style.overflow = 'auto'; // Destrava o scroll
  };

  onMounted(fetchInfoCards);
</script>

<style scoped>
  .info-container {
    padding: 60px 20px;
    background-color: #222; /* Fundo escuro da seção para destacar os cards */
    display: flex;
    flex-direction: column;
    gap: 40px;
    align-items: center;
  }

  .info-card {
    max-width: 1100px;
    width: 100%;
    min-height: 450px;
    display: flex;
    align-items: center;
    border-radius: 40px; 
    overflow: hidden;
    padding: 5px 30px ; /* Espaçamento interno para a imagem não encostar na borda */
    box-shadow: 0 10px 30px rgba(0,0,0,0.3);
    transition: transform 0.3s ease;
  }

  .info-card:hover {
    transform: scale(1.02);
  }

  .info-content {
    flex: 1.2;
    padding: 20px 40px;
  }

  .info-title {
    font-size: 2.2rem;
    line-height: 1.2;
    font-weight: 800;
    margin-bottom: 20px;
    font-family: 'Inter', sans-serif;
  }

  .info-text {
    font-size: 1.1rem;
    line-height: 1.6;
    opacity: 0.9;
  }

  .info-image-wrapper {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .info-img {
    width: 100%;
    max-height: 350px;
    object-fit: cover;
    border-radius: 30px; /* Borda arredondada na própria imagem */
  }

  /* Classe para inverter a ordem */
  .row-reverse {
    flex-direction: row-reverse;
  }

  .loading-state {
    color: white;
    font-size: 1.5rem;
    padding: 100px;
  }
  /* Limita o texto a 5 linhas e adiciona o "..." automático */
  .line-clamp {
    display: -webkit-box;
    -webkit-line-clamp: 6; /* Número de linhas antes de cortar */
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin-bottom: 15px;
  }

  .read-more-btn {
    background: none;
    border: none;
    padding: 0;
    font-weight: 700;
    text-decoration: underline;
    cursor: pointer;
    font-size: 1rem;
    opacity: 0.8;
    transition: opacity 0.2s;
  }

  .read-more-btn:hover {
    opacity: 1;
  }

  /* Estilos do Modal */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.85);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    padding: 20px;
  }

  .modal-content {
    max-width: 800px;
    width: 100%;
    max-height: 90vh;
    border-radius: 30px;
    overflow-y: auto;
    position: relative;
    box-shadow: 0 20px 50px rgba(0,0,0,0.5);
  }

  .close-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    background: rgba(0,0,0,0.2);
    border: none;
    color: inherit;
    font-size: 1.5rem;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    cursor: pointer;
    z-index: 10;
  }

  .modal-header-img {
    width: 100%;
    height: 300px;
    object-fit: cover;
  }

  .modal-body {
    padding: 40px;
  }
  /*
  .modal-title {
    font-size: 2.5rem;
    font-weight: 800;
    margin-bottom: 25px;
  }
  */
  /* Transição de entrada do modal */
  .fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
  .fade-enter-from, .fade-leave-to { opacity: 0; }

  /* Responsividade */
  @media (max-width: 900px) {
    .info-card {
      flex-direction: column !important;
      padding: 20px;
      text-align: center;
    }
    
    .info-content {
      padding: 20px 10px;
    }

    .info-image-wrapper {
      width: 100%;
    }
  }

  /* --- ADICIONE AO SEU STYLE SCOPED --- */

  @media (max-width: 1024px) {
    .info-card {
      max-width: 90%; /* Dá um respiro nas laterais em tablets */
      min-height: auto; /* Deixa a altura ser definida pelo conteúdo */
    }

    .info-title {
      font-size: 1.8rem; /* Diminui um pouco o título para não quebrar tanto */
    }
  }

  @media (max-width: 768px) {
    .info-container {
      padding: 40px 15px;
      gap: 30px;
    }

    .info-card {
      /* Forçamos o empilhamento vertical independente da classe row-reverse */
      flex-direction: column !important; 
      padding: 0; /* Remove padding lateral para a imagem encostar se quiser */
      border-radius: 25px; /* Arredondamento mais suave para telas menores */
      text-align: center;
    }

    /* Garante que a imagem sempre fique no TOPO no mobile, 
      mesmo que no desktop ela esteja na direita */
    .info-image-wrapper {
      order: -1; 
      width: 100%;
    }

    .info-img {
      border-radius: 25px 25px 0 0; /* Arredonda apenas o topo para casar com o card */
      max-height: 250px;
    }

    .info-content {
      padding: 30px 20px;
      flex: none;
    }

    .info-title {
      font-size: 1.5rem;
      margin-bottom: 15px;
    }

    .info-text {
      font-size: 1rem;
    }

    /* Ajustes do Modal para Mobile */
    .modal-content {
      max-height: 95vh;
      border-radius: 20px;
    }

    .modal-body {
      padding: 25px 20px;
    }

    .modal-header-img {
      height: 200px;
    }
  }

  @media (max-width: 480px) {
    .info-title {
      font-size: 1.3rem;
    }
    
    .info-card {
      box-shadow: 0 5px 15px rgba(0,0,0,0.2); /* Sombra mais leve para telas pequenas */
    }
  }
</style>